<template>
  <rmr-modal
    :value="value"
    variant="form"
    :icon="icons.mdiTagOutline"
    eyebrow="Opportunité Affilizz"
    :title="opportunity ? opportunity.product.title : ''"
    :subtitle="opportunity ? merchant : ''"
    max-width="860"
    content-class="ctc-opportunity-dialog"
    @input="$emit('input', $event)"
  >
    <template #body>
      <v-card-text v-if="opportunity" class="ctc-opp-detail">
        <section class="ctc-opp-detail__hero">
          <div class="ctc-opp-detail__media">
            <img
              v-if="product.imageUrl"
              :src="product.imageUrl"
              :alt="product.title"
            />
            <ctc-product-visual
              v-else
              :icon="product.visual.icon"
              :color="product.visual.color"
              size="lg"
            />
          </div>

          <div class="ctc-opp-detail__summary">
            <div class="ctc-opp-detail__meta">
              <span class="ctc-opp-detail__merchant">{{ merchant }}</span>
              <ctc-provenance-badge source="affilizz" />
            </div>

            <div class="ctc-opp-detail__price-label">Prix actuel</div>
            <div class="ctc-opp-detail__price-row">
              <strong>{{
                formatPrice(product.priceCurrent, product.currency)
              }}</strong>
              <span v-if="hasOriginal" class="ctc-opp-detail__original">
                {{ formatPrice(product.priceOriginal, product.currency) }}
              </span>
              <span v-if="discount !== null" class="ctc-opp-detail__discount">
                -{{ discount }} %
              </span>
            </div>

            <div class="ctc-opp-detail__signal">
              <span class="ctc-opp-detail__signal-icon">
                <v-icon size="20">{{ icons.mdiTrendingDown }}</v-icon>
              </span>
              <div>
                <span>Opportunité détectée</span>
                <strong>{{ opportunity.signal.label }}</strong>
              </div>
            </div>

            <div class="ctc-opp-detail__detected">
              <v-icon size="16">{{ icons.mdiClockOutline }}</v-icon>
              Détectée le {{ formatDateTime(opportunity.detectedAt) }}
            </div>
          </div>
        </section>
      </v-card-text>
    </template>

    <template #footer>
      <div class="ctc-opp-detail__footer">
        <span v-if="action && action.reason" class="ctc-opp-detail__reason">
          {{ action.reason }}
        </span>
        <rmr-action-button
          label="Fermer"
          variant="ghost"
          @click="$emit('input', false)"
        />
        <rmr-action-button
          v-if="action"
          :label="action.label"
          :icon="icons.mdiPlusCircleOutline"
          :disabled="action.disabled"
          @click="$emit('create-article', opportunity.id)"
        />
      </div>
    </template>
  </rmr-modal>
</template>

<script>
import { computed } from "@vue/composition-api";
import {
  mdiClockOutline,
  mdiPlusCircleOutline,
  mdiTagOutline,
  mdiTrendingDown,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

import {
  discountPercent,
  formatDateTime,
  formatPrice,
  merchantLabel,
} from "../ctc-model";
import CtcProductVisual from "../shared/CtcProductVisual.vue";
import CtcProvenanceBadge from "../shared/CtcProvenanceBadge.vue";

export default {
  name: "CtcOpportunityModal",
  components: {
    CtcProductVisual,
    CtcProvenanceBadge,
    RmrActionButton,
    RmrModal,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    opportunity: {
      type: Object,
      default: null,
    },
    // { label, disabled, reason } or null when no action applies
    action: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const product = computed(() =>
      props.opportunity ? props.opportunity.product : null
    );

    return {
      discount: computed(() => discountPercent(product.value)),
      formatDateTime,
      formatPrice,
      hasOriginal: computed(
        () => !!product.value && typeof product.value.priceOriginal === "number"
      ),
      icons: {
        mdiClockOutline,
        mdiPlusCircleOutline,
        mdiTagOutline,
        mdiTrendingDown,
      },
      merchant: computed(() =>
        product.value ? merchantLabel(product.value.merchant) : ""
      ),
      product,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-opp-detail {
  display: grid;
  gap: 18px;
  padding: 22px 24px 24px !important;
  background: $rmr-surface-2;
}

.ctc-opp-detail__hero {
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-xl;
  background: white;
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 24px;
  padding: 18px;
}

.ctc-opp-detail__media {
  display: grid;
  min-height: 220px;
  place-items: center;
  overflow: hidden;
  border-radius: $rmr-radius-lg;
  background: radial-gradient(
      circle at 50% 40%,
      rgba($rmr-indigo, 0.08),
      transparent 62%
    ),
    $rmr-surface-2;

  img {
    display: block;
    width: 100%;
    height: 220px;
    object-fit: contain;
    padding: 8px;
  }
}

.ctc-opp-detail__summary {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
}

.ctc-opp-detail__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.ctc-opp-detail__merchant {
  padding: 5px 10px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-pill;
  background: white;
  font-size: $rmr-fs-sm;
  font-weight: 700;
  color: $rmr-text-secondary;
}

.ctc-opp-detail__price-label {
  font-size: $rmr-fs-sm;
  font-weight: 700;
  color: $rmr-text-muted;
}

.ctc-opp-detail__price-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2px;

  > strong {
    font-size: 32px;
    line-height: 1.15;
    color: $rmr-indigo-dark;
  }
}

.ctc-opp-detail__original {
  font-size: 16px;
  color: $rmr-text-muted;
  text-decoration: line-through;
}

.ctc-opp-detail__discount {
  padding: 5px 9px;
  border-radius: $rmr-radius-pill;
  background: $rmr-danger-bg;
  font-size: $rmr-fs-sm;
  font-weight: 800;
  color: $rmr-danger;
}

.ctc-opp-detail__signal {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-top: 18px;
  padding: 12px 14px;
  border: 1px solid rgba($rmr-indigo, 0.15);
  border-radius: $rmr-radius-lg;
  background: $rmr-rpm-soft;

  > div {
    display: grid;
    gap: 2px;
  }

  span:not(.ctc-opp-detail__signal-icon) {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.35px;
    text-transform: uppercase;
    color: $rmr-text-muted;
  }

  strong {
    line-height: 1.35;
    color: $rmr-text-primary;
  }
}

.ctc-opp-detail__signal-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.ctc-opp-detail__signal-icon {
  width: 38px;
  height: 38px;
  border-radius: $rmr-radius-lg;
  background: white;
  color: $rmr-indigo;
  box-shadow: 0 4px 12px rgba($rmr-indigo, 0.12);
}

.ctc-opp-detail__detected {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: $rmr-fs-sm;
  color: $rmr-text-muted;

  .v-icon {
    color: $rmr-text-muted;
  }
}

.ctc-opp-detail__footer {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 24px;
  width: 100%;
}

.ctc-opp-detail__reason {
  margin-right: auto;
  font-size: $rmr-fs-sm;
  color: $rmr-text-muted;
}

@media (max-width: 760px) {
  .ctc-opp-detail__footer {
    padding: 12px 16px;
  }

  .ctc-opp-detail {
    padding: 14px !important;
  }

  .ctc-opp-detail__hero {
    grid-template-columns: minmax(0, 1fr);
  }

  .ctc-opp-detail__media {
    min-height: 190px;

    img {
      height: 190px;
    }
  }
}

@media (max-width: 520px) {
  .ctc-opp-detail__price-row > strong {
    font-size: 27px;
  }
}
</style>
